/**
 * Node model — the tenant's organisation structure — client adapter.
 *
 * Built 2026-08-23 from the JAI-930 wiring kit @ `a040a1d4`
 * (`NODE-SURFACE.md`, `packages/api-contracts/src/nodes.ts`).
 *
 * ── THE FOUR RULES THIS FILE EXISTS TO ENFORCE ─────────────────────────────
 *
 * 1. A node kind is TENANT DATA. Nothing here branches on a kind key, and
 *    there is no `NodeKind` union type. "Is this the tenant root" is
 *    `parent_id === null`, NEVER `kind === 'root'` — the old spelling let a row
 *    hold the root kind without the root position, and the tenant-wide-admin
 *    bypass keyed off the kind, so a row could hand out authority the tree
 *    never gave it.
 * 2. Attributes DESCRIBE, they never SCOPE. No function here filters what a
 *    user may see by an attribute value. Scope is the tree's `path` prefix and
 *    only the tree.
 * 3. `node_id` ("where") and `owner_node_id` ("which unit owns it") are two
 *    different questions. This file never collapses them into one "location".
 * 4. Localized strings have two shapes: a public RESOLVED string, or an admin
 *    base-plus-map. Never a map on a public surface.
 *
 * ── WHY THE RAW WIRE SHAPE IS PRESERVED ────────────────────────────────────
 *
 * `admin.listNodes()` is typed `Promise<Record<string, unknown>[]>`, does not
 * forward `view`/`page`/`limit`, and unwraps nothing — it returns the ENVELOPE,
 * not the array. So this adapter cannot lean on the client's typing and instead
 * validates defensively against the shapes the contract declares.
 */

/** `nodes.status` — CHECK-constrained, so the closed set holds on read too. */
export type NodeStatus = "active" | "inactive" | "setup";

/**
 * The full node object — `NodeDto`, 24 fields, from every flat-view and
 * single-node read.
 *
 * `kind` is the `node_kinds.key` STRING (joined, `null` when the kind row is
 * missing — the read is a LEFT JOIN); `kind_id` is the row id. Deliberately an
 * open `string`, not a union: a fixed enum here would be the platform
 * re-owning a vocabulary the rebuild handed to the tenant.
 */
export interface UINode {
  id: string;
  parent_id: string | null;
  code: string;
  name: string;
  kind: string | null;
  kind_id: string;
  /** Materialized path, `/a/b/c/` — leading AND trailing slash. */
  path: string;
  depth: number;
  timezone: string;
  locale: string;
  currency: string;
  country_code: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  lat: number | null;
  lng: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  settings: Record<string, unknown>;
  status: NodeStatus;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * The REDUCED shape the two tree reads return — eight keys and nothing else.
 * It is not a `UINode`: no `path`, no `depth`, no `kind_id`, no address block,
 * no timestamps. A tree view that needs those has to read the node.
 */
export interface UITreeNode {
  id: string;
  parent_id: string | null;
  code: string;
  kind: string | null;
  name: string;
  status: NodeStatus;
  settings: Record<string, unknown>;
  children: UITreeNode[];
}

/** A node kind — a row in the tenant's own vocabulary, not a platform constant. */
export interface UINodeKind {
  id: string;
  key: string;
  label: string;
  /**
   * `'place' | 'unit' | null` — GUIDANCE METADATA ONLY. It tells a feature
   * whether an address belongs on a node. It carries no authorization and no
   * structural meaning.
   */
  class: "place" | "unit" | null;
  active: boolean;
  label_base_locale?: string;
  label_translations?: Record<string, { value: string; value_kind?: string }>;
}

/** Per-key attribution for one resolved setting: the value and its source. */
export interface UISettingAttribution {
  value: unknown;
  source_node_id: string;
  source_node_name: string;
}

/**
 * The `ACTIVE_CHILDREN` advisory. Rides on a **200 with the write already
 * done** — a success, not an error.
 *
 * Its discriminator is `type`, not `code`: `code` is the closed, registry-backed
 * ERROR vocabulary, and a client that routed this into 4xx handling would tell
 * someone their change failed when it succeeded.
 */
export interface UIActiveChildrenWarning {
  type: "ACTIVE_CHILDREN";
  message: string;
  active_child_count: number;
  active_children: { id: string; name: string }[];
}

const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const nstr = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const num = (v: unknown, fallback = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const nnum = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

const STATUSES: NodeStatus[] = ["active", "inactive", "setup"];
const status = (v: unknown): NodeStatus =>
  STATUSES.includes(v as NodeStatus) ? (v as NodeStatus) : "active";

/** Normalize one full node object. */
export function toUINode(raw: unknown): UINode {
  const r = rec(raw);
  return {
    id: str(r.id),
    parent_id: nstr(r.parent_id),
    code: str(r.code),
    name: str(r.name),
    kind: nstr(r.kind),
    kind_id: str(r.kind_id),
    path: str(r.path),
    depth: num(r.depth),
    timezone: str(r.timezone, "UTC"),
    locale: str(r.locale, "en-US"),
    currency: str(r.currency, "USD"),
    country_code: nstr(r.country_code),
    address_line1: nstr(r.address_line1),
    address_line2: nstr(r.address_line2),
    city: nstr(r.city),
    postcode: nstr(r.postcode),
    lat: nnum(r.lat),
    lng: nnum(r.lng),
    phone: nstr(r.phone),
    email: nstr(r.email),
    website: nstr(r.website),
    settings: rec(r.settings),
    status: status(r.status),
    sort_order: num(r.sort_order),
    created_at: str(r.created_at),
    updated_at: str(r.updated_at),
  };
}

/** Normalize a reduced tree node, recursively. */
export function toUITreeNode(raw: unknown): UITreeNode {
  const r = rec(raw);
  const kids = Array.isArray(r.children) ? r.children : [];
  return {
    id: str(r.id),
    parent_id: nstr(r.parent_id),
    code: str(r.code),
    kind: nstr(r.kind),
    name: str(r.name),
    status: status(r.status),
    settings: rec(r.settings),
    children: kids.map(toUITreeNode),
  };
}

export function toUINodeKind(raw: unknown): UINodeKind {
  const r = rec(raw);
  const cls = r.class;
  return {
    id: str(r.id),
    key: str(r.key),
    label: str(r.label) || str(r.key),
    class: cls === "place" || cls === "unit" ? cls : null,
    active: r.active !== false,
    label_base_locale: typeof r.label_base_locale === "string" ? r.label_base_locale : undefined,
    label_translations: r.label_translations ? (rec(r.label_translations) as UINodeKind["label_translations"]) : undefined,
  };
}

/**
 * Unwrap a list envelope. `admin.listNodes()` returns the envelope rather than
 * the array, and the same operation can come back in TWO shapes — the flat
 * branch carries `pagination`, the tree branch does not — so the caller has to
 * discriminate rather than assume.
 */
export function unwrapList(raw: unknown): { items: unknown[]; pagination: Record<string, unknown> | null } {
  const env = rec(raw);
  const data = Array.isArray(env.data) ? env.data : Array.isArray(raw) ? (raw as unknown[]) : [];
  const pagination = env.pagination ? rec(env.pagination) : null;
  return { items: data, pagination };
}

/** Unwrap a single-item envelope, `{ data: … }`. */
export function unwrapItem(raw: unknown): unknown {
  const env = rec(raw);
  return "data" in env ? env.data : raw;
}

/** Read the `ACTIVE_CHILDREN` advisory off a 200 PATCH body, if present. */
export function readActiveChildrenWarning(raw: unknown): UIActiveChildrenWarning | null {
  const w = rec(rec(raw).warning);
  if (w.type !== "ACTIVE_CHILDREN") return null;
  const kids = Array.isArray(w.active_children) ? w.active_children : [];
  return {
    type: "ACTIVE_CHILDREN",
    message: str(w.message),
    active_child_count: num(w.active_child_count, kids.length),
    active_children: kids.map((k) => ({ id: str(rec(k).id), name: str(rec(k).name) })),
  };
}

/**
 * Is this the tenant root? **`parent_id === null`, never `kind === 'root'`.**
 * Rule 1 — the single most load-bearing line in this file.
 */
export const isTenantRoot = (n: { parent_id: string | null }): boolean => n.parent_id === null;

/**
 * Path segments, from `'/a/b/c/'`. The path has a leading AND trailing slash,
 * so a naive `split('/')` yields empty strings at both ends.
 */
export const pathSegments = (path: string): string[] => path.split("/").filter(Boolean);

/**
 * Would moving `node` under `newParent` be a cycle? Cheaply predictable
 * client-side from `path` — which is the difference between a drag refused on
 * drop and a drop target that was never offered.
 *
 * Two cases, which the API reports under ONE code (`CYCLE_DETECTED`) and
 * distinguishes only in the message: self-parenting, and a new parent inside
 * the moved subtree.
 */
export function wouldCycle(node: { id: string; path: string }, newParent: { id: string; path: string }): boolean {
  if (node.id === newParent.id) return true;
  return newParent.path.startsWith(node.path);
}

/** The depth cap is a CHECK constraint, not a level ladder. */
export const NODE_DEPTH_CAP = 12;

/**
 * Would the move exceed the depth cap? Computed against the **deepest row of
 * the moved subtree**, not the moved node — so a shallow drag can still be
 * refused.
 */
export function wouldExceedDepth(subtreeMaxDepth: number, movedDepth: number, newParentDepth: number): boolean {
  const delta = newParentDepth + 1 - movedDepth;
  return subtreeMaxDepth + delta > NODE_DEPTH_CAP;
}

/** Deepest `depth` within a node's subtree, from the flat list. */
export function subtreeMaxDepth(all: UINode[], node: UINode): number {
  return all.reduce((max, n) => (n.path.startsWith(node.path) && n.depth > max ? n.depth : max), node.depth);
}

/** `nodes.code` — the slug the worker's `CreateNodeSchema` enforces. */
export const NODE_CODE_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function nodeCodeIssue(code: string): string | null {
  if (!code) return "A code is required.";
  if (code.length > 63) return "A code is at most 63 characters.";
  if (!NODE_CODE_PATTERN.test(code)) {
    return "Lower-case letters, digits and hyphens only, starting and ending with a letter or digit.";
  }
  return null;
}

/**
 * Node-tree error codes, per route.
 *
 * ⚠️ **None of these is in the platform error-code registry** — they are
 * emitted as inline envelopes by `nodeTreeErrorResponse`. This table is the
 * vocabulary.
 *
 * Codes deliberately ABSENT because they cannot fire on these routes:
 * `INVALID_ID`/`DUPLICATE_ID` on create (the router mints the id with
 * `ulid()`, so no request body can supply one), and `DUPLICATE_ID`/`ROOT_EXISTS`
 * on reparent (never raised by that command). Building handling for them would
 * be building dead paths.
 */
export const NODE_ERROR_COPY: Record<string, string> = {
  VALIDATION_ERROR: "That didn't pass validation.",
  PARENT_NOT_FOUND: "That parent doesn't exist. A malformed id reports the same way — it simply resolves to no row.",
  DEPTH_CAP_EXCEEDED: `That would place a node past depth ${NODE_DEPTH_CAP}.`,
  ROOT_EXISTS: "This tenant already has a root. There can be only one.",
  CONCURRENT_MODIFICATION: "Someone else changed the tree while this was saving. Try again.",
  INVALID_ID: "That id is empty or contains a slash — an id is used as a path segment.",
  NODE_NOT_FOUND: "That node doesn't exist.",
  ROOT_IMMOVABLE: "The tenant root can't be moved.",
  CYCLE_DETECTED: "A node can't be moved inside itself or its own subtree.",
};

/**
 * Codes reachable ONLY on a corrupted tree. Not user-correctable, and not
 * something to design a form error for — the honest response is to report it
 * and name the invariant sweep.
 */
export const NODE_CORRUPTION_CODES = [
  "PARENT_NOT_INDEXED",
  "NODE_NOT_INDEXED",
  "PATH_DOES_NOT_NAME_ITS_ROW",
  "PARENT_CHAIN_UNRESOLVED",
];

/**
 * Render an API error. An unknown `kind` on write returns a 400 whose message
 * **lists the keys the tenant actually has** — so the message is more useful
 * than any generic mapping, and is preferred whenever present.
 */
export function nodeErrorMessage(raw: unknown): string {
  const err = rec(rec(raw).error);
  const code = str(err.code);
  const message = str(err.message);
  if (NODE_CORRUPTION_CODES.includes(code)) {
    return `${message || code} — this tenant's tree has a broken invariant. Report it and run the node-tree invariant sweep; it isn't correctable from here.`;
  }
  return message || NODE_ERROR_COPY[code] || code || "That didn't work.";
}

/** Is this code worth offering a retry for? */
export const isRetryable = (raw: unknown): boolean =>
  str(rec(rec(raw).error).code) === "CONCURRENT_MODIFICATION";

/**
 * NOTE — there is deliberately NO client-side tree builder here any more.
 *
 * The first cut of this file had one, because `listNodes()` appeared to offer
 * only a flat list. It does not: it takes `{ view: 'tree' }`. Rebuilding the
 * tree locally was a workaround for an api-client that predated the node-model
 * kit, and keeping it would mean this file quietly asserting its own answer to
 * a question the endpoint already answers — the same class of mistake as
 * re-deriving the settings cascade instead of reading `attribution`.
 */
